import React, { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { getAdminLicense } from '../../api/license';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import {
  LICENSE_PLANS,
  LICENSE_FEATURE_ROWS,
  catalogTierForEdition,
  formatTierCaps,
  planIncludesFeature,
  planLabel,
} from '../../constants/licenseTiers';
import { getUiLocaleTag } from '../../utils/regionalFormat';

function formatLimit(max, t) {
  if (max == null || max < 0) return t('settings.license.unlimited');
  return Number(max).toLocaleString(getUiLocaleTag());
}

function usagePercent(used, max) {
  if (max == null || max < 0 || max === 0) return null;
  return Math.min(100, Math.round((used / max) * 100));
}

function CapacityRow({ label, used, max, remaining, t }) {
  const pct = usagePercent(used, max);
  const atLimit = max >= 0 && used >= max;
  const nearLimit = max >= 0 && remaining >= 0 && remaining <= Math.max(1, Math.floor(max * 0.1));

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-900">{label}</p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">
            {used}
            <span className="text-base font-normal text-slate-500">
              {' '}/ {formatLimit(max, t)}
            </span>
          </p>
          {max >= 0 && (
            <p className="mt-1 text-xs text-slate-500">
              {t('settings.license.slotsRemaining', { count: remaining })}
            </p>
          )}
        </div>
        {atLimit && (
          <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
            {t('settings.license.atLimit')}
          </span>
        )}
        {!atLimit && nearLimit && (
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
            {t('settings.license.nearLimit')}
          </span>
        )}
      </div>
      {pct != null && (
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            className={`h-full rounded-full transition-all ${
              atLimit ? 'bg-red-500' : nearLimit ? 'bg-amber-500' : 'bg-primary-600'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

function FeatureCell({ included, highlight }) {
  if (included) {
    return (
      <td className={`px-3 py-2.5 text-center text-sm ${highlight ? 'bg-primary-50/70' : ''}`}>
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
          ✓
        </span>
      </td>
    );
  }
  return (
    <td className={`px-3 py-2.5 text-center text-sm text-slate-300 ${highlight ? 'bg-primary-50/40' : ''}`}>
      —
    </td>
  );
}

function PlanComparisonTable({ currentEdition, t }) {
  const activeId = String(currentEdition || '').toUpperCase();

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
          {t('settings.license.stepFour')}
        </p>
        <h2 className="mt-1 text-lg font-semibold text-slate-900">{t('settings.license.plansTitle')}</h2>
        <p className="mt-1 max-w-2xl text-sm text-slate-600">{t('settings.license.plansDesc')}</p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="sticky left-0 z-10 min-w-[200px] bg-slate-50 px-4 py-3 font-medium text-slate-700">
                &nbsp;
              </th>
              {LICENSE_PLANS.map((plan) => {
                const isCurrent = plan.id === activeId;
                return (
                  <th
                    key={plan.id}
                    className={`min-w-[120px] px-3 py-3 text-center ${isCurrent ? 'bg-primary-100/80' : ''}`}
                  >
                    <div className="font-semibold text-slate-900">{planLabel(plan, t)}</div>
                    <div className="mt-0.5 text-xs font-normal text-slate-500">{plan.id}</div>
                    {isCurrent && (
                      <span className="mt-1.5 inline-block rounded-full bg-primary-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                        {t('settings.license.yourPlan')}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-100 bg-slate-50/60">
              <td
                colSpan={1 + LICENSE_PLANS.length}
                className="sticky left-0 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
              >
                {t('settings.license.capacityLimits')}
              </td>
            </tr>
            {[
              { key: 'students', label: t('settings.license.activeStudents') },
              { key: 'branches', label: t('settings.license.branches') },
              { key: 'coaches', label: t('settings.license.coaches') },
            ].map((row) => (
              <tr key={row.key} className="border-b border-slate-50">
                <td className="sticky left-0 z-10 bg-white px-4 py-2.5 font-medium text-slate-700">
                  {row.label}
                </td>
                {LICENSE_PLANS.map((plan) => (
                  <td
                    key={plan.id}
                    className={`px-3 py-2.5 text-center font-medium text-slate-900 ${
                      plan.id === activeId ? 'bg-primary-50/70' : ''
                    }`}
                  >
                    {plan[row.key].toLocaleString(getUiLocaleTag())}
                  </td>
                ))}
              </tr>
            ))}

            {LICENSE_FEATURE_ROWS.map((section) => (
              <React.Fragment key={section.groupKey}>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <td
                    colSpan={1 + LICENSE_PLANS.length}
                    className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
                  >
                    {t(section.groupKey, { defaultValue: section.groupKey })}
                  </td>
                </tr>
                {section.items.map((feature) => (
                  <tr key={feature.labelKey} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="sticky left-0 z-10 bg-white px-4 py-2.5 text-slate-700">
                      {t(feature.labelKey, { defaultValue: feature.labelKey })}
                    </td>
                    {LICENSE_PLANS.map((plan) => (
                      <FeatureCell
                        key={plan.id}
                        included={planIncludesFeature(plan.id, feature.tiers)}
                        highlight={plan.id === activeId}
                      />
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function LicenseSettings() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminLicense()
      .then(setData)
      .catch(() => setError(t('settings.license.loadFailed')))
      .finally(() => setLoading(false));
  }, [t]);

  if (loading) {
    return (
      <PageLoadingShell
        variant="page-shell-form"
        title={t('settings.license.title')}
        subtitle={t('settings.license.subtitle')}
      />
    );
  }

  const edition = data?.edition?.trim() || t('settings.license.notSpecified');
  const catalogTier = catalogTierForEdition(edition) || data?.catalogTier;
  const limits = data?.limits || {};
  const usage = data?.usage || {};
  const remaining = data?.remaining || {};
  const activeEdition = edition !== t('settings.license.notSpecified') ? edition : '';

  return (
    <div className="page-shell-form">
      <PageHeaderSection
        eyebrow={t('nav.system')}
        title={t('settings.license.title')}
        subtitle={t('settings.license.subtitle')}
      />

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {data && (
        <div className="space-y-6">
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                {t('settings.license.stepOne')}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">{t('settings.license.yourLicense')}</h2>
              <p className="mt-1 max-w-2xl text-sm text-slate-600">{t('settings.license.yourLicenseDesc')}</p>
            </div>
            <div className="grid gap-4 px-5 py-5 sm:grid-cols-2 sm:px-6">
              <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('settings.license.edition')}</p>
                <p className="mt-1 text-lg font-semibold text-slate-900">{edition}</p>
                {catalogTier && (
                  <p className="mt-1 text-xs text-slate-500">
                    {t('settings.license.standardPlan', { edition, caps: formatTierCaps(catalogTier, t) })}
                  </p>
                )}
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('settings.license.expiry')}</p>
                <p className="mt-1 text-lg font-semibold text-slate-900">
                  {data.daysRemaining >= 0
                    ? t('settings.license.daysRemaining', { count: data.daysRemaining })
                    : '—'}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 sm:col-span-2">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('settings.license.licenseKey')}</p>
                <p className="mt-1 font-mono text-sm text-slate-900">{data.maskedKey || '—'}</p>
              </div>
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                {t('settings.license.stepTwo')}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">{t('settings.license.capacityTitle')}</h2>
              <p className="mt-1 max-w-2xl text-sm text-slate-600">
                <Trans i18nKey="settings.license.capacityNote" components={{ strong: <strong /> }} />
              </p>
            </div>
            <div className="grid gap-4 px-5 py-5 sm:grid-cols-3 sm:px-6">
              <CapacityRow
                t={t}
                label={t('settings.license.students')}
                used={usage.students ?? 0}
                max={limits.maxStudents}
                remaining={remaining.students}
              />
              <CapacityRow
                t={t}
                label={t('settings.license.branches')}
                used={usage.branches ?? 0}
                max={limits.maxBranches}
                remaining={remaining.branches}
              />
              <CapacityRow
                t={t}
                label={t('settings.license.coaches')}
                used={usage.coaches ?? 0}
                max={limits.maxCoaches}
                remaining={remaining.coaches}
              />
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                {t('settings.license.stepThree')}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">{t('settings.license.featuresTitle')}</h2>
            </div>
            <ul className="space-y-2 px-5 py-5 text-sm text-slate-700 sm:px-6">
              <li className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">{t('settings.license.featureCore')}</li>
              <li className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
                {t('settings.license.featureBranding')}{' '}
                {data.featureFlags?.customBranding ? (
                  <span className="font-medium text-emerald-700">{t('settings.license.included')}</span>
                ) : (
                  <span className="text-slate-500">{t('settings.license.enterpriseOnly')}</span>
                )}
              </li>
              <li className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
                {t('settings.license.featureWhatsapp')}{' '}
                {data.featureFlags?.whatsapp ? (
                  <span className="font-medium text-emerald-700">{t('settings.license.included')}</span>
                ) : (
                  <span className="text-slate-500">{t('settings.license.proEnterprise')}</span>
                )}
              </li>
              <li className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
                {t('settings.license.featureBulkImport')}{' '}
                {edition.toUpperCase() === 'ENTERPRISE' ? (
                  <span className="font-medium text-emerald-700">{t('settings.license.included')}</span>
                ) : (
                  <span className="text-slate-500">{t('settings.license.enterpriseOnly')}</span>
                )}
              </li>
            </ul>
          </section>
        </div>
      )}

      <div className="mt-6">
        <PlanComparisonTable currentEdition={activeEdition} t={t} />
      </div>
    </div>
  );
}
